import type { SVGProps } from 'react';

/**
 * Conjunto de icones da aplicacao.
 *
 * Os icones sao desenhados aqui em vez de virem de uma biblioteca porque a
 * quantidade usada e pequena e previsivel. Isso evita carregar um pacote inteiro
 * para exibir trinta simbolos e mantem o traco consistente com a marca.
 *
 * O desenho segue a grade de 24 com margem de 3, traco de 1,8 com ponta e juncao
 * arredondadas, e cantos curvos nas formas: o mesmo desenho linear dos paineis
 * atuais. Cada icone e um caminho so, para que a cor venha do texto ao redor
 * (`currentColor`) e o chip colorido (`IconChip`) pinte o icone pelo sentido.
 */

export type IconName =
  | 'dashboard'
  | 'clock'
  | 'users'
  | 'briefcase'
  | 'coins'
  | 'shield'
  | 'gavel'
  | 'target'
  | 'file'
  | 'stamp'
  | 'signature'
  | 'send'
  | 'bank'
  | 'refresh'
  | 'close'
  | 'alert'
  | 'percent'
  | 'layers'
  | 'copy'
  | 'lock'
  | 'plug'
  | 'settings'
  | 'logout'
  | 'menu'
  | 'chevronLeft'
  | 'chevronRight'
  | 'check'
  | 'sparkle'
  | 'chart'
  | 'eye'
  | 'search'
  | 'info'
  | 'pencil';

const paths: Record<IconName, string> = {
  dashboard:
    'M5.25 3.5h3.5a1.75 1.75 0 0 1 1.75 1.75v4.5a1.75 1.75 0 0 1 -1.75 1.75h-3.5a1.75 1.75 0 0 1 -1.75 -1.75v-4.5a1.75 1.75 0 0 1 1.75 -1.75ZM15.25 3.5h3.5a1.75 1.75 0 0 1 1.75 1.75v1.5a1.75 1.75 0 0 1 -1.75 1.75h-3.5a1.75 1.75 0 0 1 -1.75 -1.75v-1.5a1.75 1.75 0 0 1 1.75 -1.75ZM15.25 11.5h3.5a1.75 1.75 0 0 1 1.75 1.75v5.5a1.75 1.75 0 0 1 -1.75 1.75h-3.5a1.75 1.75 0 0 1 -1.75 -1.75v-5.5a1.75 1.75 0 0 1 1.75 -1.75ZM5.25 14.5h3.5a1.75 1.75 0 0 1 1.75 1.75v2.5a1.75 1.75 0 0 1 -1.75 1.75h-3.5a1.75 1.75 0 0 1 -1.75 -1.75v-2.5a1.75 1.75 0 0 1 1.75 -1.75Z',
  clock: 'M3.25 12a8.75 8.75 0 1 0 17.5 0a8.75 8.75 0 1 0 -17.5 0ZM12 7.25V12l3.25 2',
  users:
    'M6 8a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0ZM3.25 20c.4-3.3 2.9-5.5 6.25-5.5s5.85 2.2 6.25 5.5M16 4.75a3.5 3.5 0 0 1 0 6.5M18.25 14.9c1.45.75 2.35 2.35 2.5 5.1',
  briefcase:
    'M5.5 7h13a2.5 2.5 0 0 1 2.5 2.5v8a2.5 2.5 0 0 1 -2.5 2.5h-13a2.5 2.5 0 0 1 -2.5 -2.5v-8a2.5 2.5 0 0 1 2.5 -2.5ZM8.75 7V5.75a2.25 2.25 0 0 1 2.25-2.25h2a2.25 2.25 0 0 1 2.25 2.25V7M3 12.5h18M10.5 12.5v1.5h3v-1.5',
  coins:
    'M3.25 12a8.75 8.75 0 1 0 17.5 0a8.75 8.75 0 1 0 -17.5 0ZM14.75 9.25c-.45-.95-1.5-1.6-2.75-1.6-1.65 0-2.75.85-2.75 2.1 0 2.85 5.75 1.45 5.75 4.35 0 1.3-1.2 2.2-3 2.2-1.35 0-2.45-.6-3-1.6M12 6.1v1.55M12 16.4v1.5',
  shield:
    'M12 2.9c2.35 1.45 4.75 2.25 7.25 2.45v6.15c0 4.55-2.9 7.95-7.25 9.6-4.35-1.65-7.25-5.05-7.25-9.6V5.35C7.25 5.15 9.65 4.35 12 2.9ZM8.9 12l2.2 2.2 4.1-4.3',
  gavel: 'M13.6 3.4 20.6 10.4 17.4 13.6 10.4 6.6ZM13.9 10.1 4.6 19.4M3 21h8.5',
  target:
    'M3.25 12a8.75 8.75 0 1 0 17.5 0a8.75 8.75 0 1 0 -17.5 0ZM7 12a5 5 0 1 0 10 0a5 5 0 1 0 -10 0ZM10.75 12a1.25 1.25 0 1 0 2.5 0a1.25 1.25 0 1 0 -2.5 0Z',
  file: 'M13.75 3H7.5A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h9a2.25 2.25 0 0 0 2.25-2.25V8ZM13.75 3v3.75A1.25 1.25 0 0 0 15 8h3.75M8.75 12.75h6.5M8.75 16.25h4.5',
  stamp:
    'M9.25 6a2.75 2.75 0 1 0 5.5 0a2.75 2.75 0 1 0 -5.5 0ZM10.6 8.4 10 12.75M13.4 8.4 14 12.75M6 12.75h12a1.75 1.75 0 0 1 1.75 1.75v1.25a1.75 1.75 0 0 1 -1.75 1.75h-12a1.75 1.75 0 0 1 -1.75 -1.75v-1.25a1.75 1.75 0 0 1 1.75 -1.75ZM6.5 20.75h11',
  signature:
    'M3 17.25c2.1-3.5 3.6-6.25 5-6.25 2.2 0 .1 6 2.35 6 1.45 0 2.55-4.75 4.4-4.75 1.55 0 1.15 3.5 2.75 3.5.9 0 1.6-.55 2.5-1.25M3 21h18',
  send: 'M21 3 10.5 13.5M21 3 14.6 20.6a.55.55 0 0 1-1 .05L10.5 13.5 3.35 10.4a.55.55 0 0 1 .05-1Z',
  bank: 'M3.5 9.25 12 4l8.5 5.25ZM6 12v5M10 12v5M14 12v5M18 12v5M4.5 17h15M3 20.5h18',
  refresh:
    'M3.75 12a8.25 8.25 0 0 1 14.2-5.7L20.25 8.5M20.25 3.75V8.5H15.5M20.25 12a8.25 8.25 0 0 1-14.2 5.7L3.75 15.5M3.75 20.25V15.5H8.5',
  close: 'M18 6 6 18M6 6l12 12',
  alert:
    'M10.27 4 2.75 17.25A2 2 0 0 0 4.5 20.25h15a2 2 0 0 0 1.75-3L13.73 4a2 2 0 0 0-3.46 0ZM12 9.25v4M12 16.75h.01',
  percent:
    'M18.5 5.5l-13 13M4.75 7a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0ZM14.75 17a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0Z',
  layers:
    'M12.85 3.2a1.9 1.9 0 0 0-1.7 0L3.6 6.9a.75.75 0 0 0 0 1.35l7.55 3.7a1.9 1.9 0 0 0 1.7 0l7.55-3.7a.75.75 0 0 0 0-1.35ZM3.25 12.1l7.9 3.85a1.9 1.9 0 0 0 1.7 0l7.9-3.85M3.25 16.35l7.9 3.85a1.9 1.9 0 0 0 1.7 0l7.9-3.85',
  copy: 'M10.75 8.5h8a2.25 2.25 0 0 1 2.25 2.25v8a2.25 2.25 0 0 1 -2.25 2.25h-8a2.25 2.25 0 0 1 -2.25 -2.25v-8a2.25 2.25 0 0 1 2.25 -2.25ZM15.5 8.5V5.25A2.25 2.25 0 0 0 13.25 3h-8A2.25 2.25 0 0 0 3 5.25v8a2.25 2.25 0 0 0 2.25 2.25H8.5',
  lock: 'M6.75 10.5h10.5a2.25 2.25 0 0 1 2.25 2.25v6a2.25 2.25 0 0 1 -2.25 2.25h-10.5a2.25 2.25 0 0 1 -2.25 -2.25v-6a2.25 2.25 0 0 1 2.25 -2.25ZM8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.75v2',
  plug: 'M9 3v4.5M15 3v4.5M6.5 7.5h11v3a5.5 5.5 0 0 1-11 0ZM12 16v5',
  settings:
    'M20 7.25h-8.5M7.5 7.25H4M20 16.75h-3.5M12.5 16.75H4M7.25 7.25a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0ZM12.25 16.75a2.25 2.25 0 1 0 4.5 0a2.25 2.25 0 1 0 -4.5 0Z',
  logout:
    'M9.5 20.5H6.25A2.25 2.25 0 0 1 4 18.25V5.75A2.25 2.25 0 0 1 6.25 3.5H9.5M15.5 16.5 20 12l-4.5-4.5M20 12H9.5',
  menu: 'M4 6.5h16M4 12h16M4 17.5h16',
  chevronLeft: 'm14.5 18-6-6 6-6',
  chevronRight: 'm9.5 18 6-6-6-6',
  check: 'M20 6.5 9.25 17.25 4 12',
  sparkle:
    'M10.5 4.5c.55 4.3 2.7 6.45 7 7-4.3.55-6.45 2.7-7 7-.55-4.3-2.7-6.45-7-7 4.3-.55 6.45-2.7 7-7ZM18.5 2.75v3.5M16.75 4.5h3.5M19 17.25v2.5M17.75 18.5h2.5',
  // Relatorios (P-BI-1): colunas sobre os eixos.
  chart:
    'M3.5 3.5v15.25A1.75 1.75 0 0 0 5.25 20.5H20.5M8.5 16.5v-4M13 16.5V7.5M17.5 16.5v-6.5',
  eye: 'M2.4 12.35a1 1 0 0 1 0-.7C3.9 7.9 7.6 5.25 12 5.25s8.1 2.65 9.6 6.4a1 1 0 0 1 0 .7c-1.5 3.75-5.2 6.4-9.6 6.4s-8.1-2.65-9.6-6.4ZM9 12a3 3 0 1 0 6 0a3 3 0 1 0 -6 0Z',
  // A lupa da busca das listas.
  search:
    'M4 10.75a6.75 6.75 0 1 0 13.5 0a6.75 6.75 0 1 0 -13.5 0ZM20.25 20.25 15.5 15.5',
  // O "i" do aviso informativo.
  info: 'M3.25 12a8.75 8.75 0 1 0 17.5 0a8.75 8.75 0 1 0 -17.5 0ZM12 11v5.25M12 7.75h.01',
  // O lapis da edicao.
  pencil:
    'M16.4 3.85a2.3 2.3 0 0 1 3.25 3.25L8 18.75 3.5 20.5l1.75-4.5ZM14.25 6l3.75 3.75',
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 18, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={paths[name]} />
    </svg>
  );
}
